import React, { useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/hooks';
import { Lx } from '../../../../../i18n/consts';
import FormDateSelector from '../../../../../components/shared/nextgen/form/form-date-selector';
import { FormInput } from '../../../../../components/shared/form-controls/form-input';
import { FormNumberInput } from '../../../../../components/shared/nextgen/form/form-number-input';
import MultipleFileUploader from '../multiple-file-uploader';
import { Cost, CostToCreate, MAX_COST_AMOUNT, MIN_COST_AMOUNT } from '../../../../../features/cost-report/types';
import { createCost } from '../../../../../features/cost-report/slice';
import { IsValidExtension, ObjectsToFormDataCoverter } from '../helpers';
import BaseForm from '../../../../../components/shared/form-controls/base-form';
import { NAVIGATION_ROUTES } from '../../../../../routes/NavigationRoute';
import { REmployees } from '../../../../../features/roles/types';
import { PIdNameModel } from '../../../../../features/projects/types';
import { DropdownField } from '../../../../../components/shared/nextgen/dropdown-field';
import { getAllEmployees } from '../../../../../features/employees/short';
import { getProjects } from '../../../../../features/projects/filter';
import { canManageOtherCosts, canSeeCostReportPage } from '../../../../../features/profile/entityPermissions';

interface CreateCostState {
    defaults?: Partial<Cost>;
    employees?: REmployees[];
    projects?: PIdNameModel[];
}

interface CreateForm {
    employee: REmployees | null;
    date: string;
    project?: PIdNameModel | null;
    note?: string;
    amount: number;
    files?: File[];
}

const CreateCostForm: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const intl = useIntl();
    const location = useLocation();
    const profile = useAppSelector((selector) => selector.profile);
    const employees = useAppSelector((selector) => selector.rEmployees);
    const projects = useAppSelector((selector) => selector.eProjects);
    const state = location.state as CreateCostState | undefined;
    const formikRef = useRef<FormikProps<CreateForm>>(null);
    const canViewCostReport = canSeeCostReportPage(profile);
    const canEditOtherCosts = canManageOtherCosts(profile);
    const minEditableDate = useMemo(
        () => canEditOtherCosts ? undefined : moment().startOf('month'),
        [canEditOtherCosts]
    );
    const maxEditableDate = useMemo(
        () => canEditOtherCosts ? undefined : moment().startOf('day'),
        [canEditOtherCosts]
    );
    const ownEmployee = useMemo<REmployees | null>(() => {
        if (!profile.id) {
            return null;
        }

        return employees.find((employee) => employee.id === profile.id)
            ?? { id: profile.id, name: profile.name, isDefault: false };
    }, [employees, profile.id, profile.name]);
    const employeeOptions = useMemo<REmployees[]>(() => {
        const source = state?.employees ?? employees;

        if (canEditOtherCosts) {
            return source;
        }

        const ownEmployeeFromSource = source.find((employee) => employee.id === profile.id);
        return ownEmployeeFromSource ? [ownEmployeeFromSource] : ownEmployee ? [ownEmployee] : [];
    }, [canEditOtherCosts, employees, ownEmployee, profile.id, state?.employees]);
    const projectOptions = state?.projects ?? projects;

    useEffect(() => {
        if (!canViewCostReport) {
            navigate(NAVIGATION_ROUTES.COSTREPORT, { replace: true });
            return;
        }

        if (canEditOtherCosts && employeeOptions.length < 1) {
            dispatch(getAllEmployees());
        }

        if (projectOptions.length < 1) {
            dispatch(getProjects());
        }
    }, [canEditOtherCosts, canViewCostReport, dispatch, employeeOptions.length, navigate, projectOptions.length]);

    const initialValues = useMemo<CreateForm>(() => {
        const defaults = state?.defaults;
        const requestedDate = defaults?.date?.trim() ? moment(defaults.date, 'YYYY-MM-DD', true) : moment();
        const defaultDate = !canEditOtherCosts && requestedDate.isValid() && minEditableDate && maxEditableDate
            ? moment.min(moment.max(requestedDate, minEditableDate), maxEditableDate).format('YYYY-MM-DD')
            : (requestedDate.isValid() ? requestedDate : moment()).format('YYYY-MM-DD');
        const defaultAmount = typeof defaults?.amount === 'number' && defaults.amount >= MIN_COST_AMOUNT
            ? defaults.amount
            : 1;
        const defaultEmployee = employeeOptions.find((employee) => employee.id === (canEditOtherCosts ? defaults?.employeeId : profile.id))
            ?? employeeOptions.find((employee) => employee.id === profile.id)
            ?? employeeOptions.find((employee) => employee.isDefault)
            ?? employeeOptions[0]
            ?? null;
        const defaultProject = projectOptions.find((project) => project.id === defaults?.projectId)
            ?? projectOptions.find((project) => project.isDefault)
            ?? null;

        return {
            employee: defaultEmployee,
            date: defaultDate,
            project: defaultProject,
            note: defaults?.note ?? '',
            amount: defaultAmount,
            files: []
        };
    }, [canEditOtherCosts, employeeOptions, maxEditableDate, minEditableDate, profile.id, projectOptions, state?.defaults]);

    const validationScheme: Yup.Schema<CreateForm> = Yup.object().shape({
        employee: Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required(),
            isDefault: Yup.boolean().required()
        }).nullable().required(),
        date: Yup.string()
            .required()
            .test('min-date', '', (value) => {
                if (!value || !minEditableDate) {
                    return true;
                }

                return !moment(value, 'YYYY-MM-DD', true).isBefore(minEditableDate, 'day');
            })
            .test('max-date', '', (value) => {
                if (!value || !maxEditableDate) {
                    return true;
                }

                return !moment(value, 'YYYY-MM-DD', true).isAfter(maxEditableDate, 'day');
            }),
        project: Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required(),
            isDefault: Yup.boolean().required()
        }).nullable().optional(),
        amount: Yup.number().required().max(MAX_COST_AMOUNT).min(MIN_COST_AMOUNT),
        note: Yup.string().optional(),
        files: Yup.array().optional()
            .test(
                'max-files',
                intl.formatMessage({ id: Lx.Cost.INVALID_FILES_COUNT }),
                (value) => {
                    return !value || (Array.isArray(value) && value.length <= 10)
                }
            )
            .test(
                'file-types',
                intl.formatMessage({ id: Lx.Cost.INVALID_FILES_EXTENSION }),
                (value) => {
                    if (!value) {
                        return true;
                    }

                    return Array.isArray(value) && value.every(file => IsValidExtension(file));
                }
            )
    });

    const handleSubmit = async (values: CreateForm) => {
        if (!values.employee) {
            return;
        }
        if (!canEditOtherCosts && values.employee.id !== profile.id) {
            return;
        }

        const cost: CostToCreate = {
            id: 0,
            employeeId: values.employee.id,
            date: values.date,
            note: values.note,
            amount: values.amount,
            projectId: values.project?.id,
            files: values.files
        };

        await dispatch(createCost(ObjectsToFormDataCoverter(cost))).unwrap();
        navigate(NAVIGATION_ROUTES.COSTREPORT);
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationScheme}
            validateOnBlur={true}
            validateOnChange={true}
            innerRef={formikRef}
            enableReinitialize
            onSubmit={handleSubmit}>
            <Form>
                <BaseForm
                    contentGap={24}
                    formType='create'
                    formHeader={Lx.Cost.ADD}
                    savingTitle={intl.formatMessage({ id: Lx.General.COST })}
                    onClose={() => navigate(NAVIGATION_ROUTES.COSTREPORT)}
                    onAfterSave={() => navigate(NAVIGATION_ROUTES.COSTREPORT)}>
                    {canEditOtherCosts && (
                        <div style={{ width: '100%', maxWidth: '504px' }}>
                            <DropdownField
                                label={intl.formatMessage({ id: Lx.General.EMPLOYEE })}
                                name={'employee'}
                                items={employeeOptions}
                                required
                                placeholder={intl.formatMessage({ id: Lx.General.CHOOSE_EMPLOYEE })} />
                        </div>
                    )}
                    <div style={{ width: '100%', maxWidth: '504px' }}>
                        <FormDateSelector
                            label={intl.formatMessage({ id: Lx.General.DATE })}
                            name={'date'}
                            minDate={minEditableDate}
                            maxDate={maxEditableDate}
                            required />
                    </div>
                    <FormNumberInput
                        max={MAX_COST_AMOUNT}
                        min={MIN_COST_AMOUNT}
                        step={1}
                        label={intl.formatMessage({ id: Lx.Cost.AMOUNT })}
                        name={'amount'}
                        placeholder={intl.formatMessage({ id: Lx.Cost.AMOUNT })}
                        required
                        style={{ width: '100%', maxWidth: '504px' }}
                    />
                    <div style={{ width: '100%', maxWidth: '504px' }}>
                        <DropdownField
                            required={false}
                            label={intl.formatMessage({ id: Lx.General.PROJECT })}
                            name={'project'}
                            items={projectOptions}
                            placeholder={intl.formatMessage({ id: Lx.General.PROJECT })} />
                    </div>
                    <FormInput
                        label={intl.formatMessage({ id: Lx.Cost.NOTE })}
                        name={'note'}
                        placeholder={intl.formatMessage({ id: Lx.Cost.NOTE })}
                        style={{ width: '100%', maxWidth: '504px' }} />
                   
                    <div style={{ width: '100%', maxWidth: '504px' }}>
                        <MultipleFileUploader
                            label={intl.formatMessage({ id: Lx.Cost.UPLOAD_FILES })}
                            hint={intl.formatMessage({ id: Lx.Cost.UPLOAD_FILES_HINT })}
                            name={'files'}
                        />
                    </div>
                </BaseForm>
            </Form>
        </Formik>
    );
};

export default CreateCostForm;
